import { db } from '../lib/postgrest'
import { SPECIAL_SCHEMA, SPECIAL_TABLES } from '../lib/specialSchema'
import type { Database } from '../types/database.generated'
import type { ServiceResult } from '../types/productsServices'

type User = Database[typeof SPECIAL_SCHEMA]['Tables'][typeof SPECIAL_TABLES.users]['Row']

const users = () => db.schema(SPECIAL_SCHEMA).from(SPECIAL_TABLES.users)

/**
 * Fetch a single user row by its primary key.
 *
 * @param id - The internal user id.
 * @returns The user row, or `null` if not found.
 * @throws {PostgrestError} on network or query errors.
 */
export async function getCurrentUser(id: string): Promise<User | null> {
  const { data, error } = await users()
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // no rows returned
    throw error
  }

  return data as User
}

/**
 * Fetch a single user row using the auth provider identifier (JWT `sub`).
 *
 * @param authProviderId - The provider user id from Clerk.
 * @returns The mapped user row, or `null` if not found.
 * @throws {PostgrestError} on network or query errors.
 */
export async function getCurrentUserByAuthProviderId(authProviderId: string): Promise<User | null> {
  const { data, error } = await users()
    .select('*')
    .eq('auth_provider_id', authProviderId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // no rows returned
    throw error
  }

  return data as User
}

type UserUpdate = Database[typeof SPECIAL_SCHEMA]['Tables'][typeof SPECIAL_TABLES.users]['Update']

export async function updateUser(id: string, patch: Omit<UserUpdate, 'id' | 'auth_provider_id' | 'email' | 'created_at' | 'updated_at'>): Promise<ServiceResult<User>> {
  const { data, error } = await users()
    .update(patch)
    .eq('id', id)
    .select('*')
    .single()

  if (error) return { data: null, error: error.message }
  return { data: data as User, error: null }
}
